import type {
  BenchmarkScope,
  Candidate,
  FieldCatalogue,
  RateRange,
  SourcingAnalysis,
  SourcingNarrative,
} from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleCheck, CircleHelp, Factory, Info, RefreshCw, Users } from "lucide-react";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useRefreshSourcing, useSourcing } from "./api";

/**
 * The buyer's sourcing analysis (WP-3.2, ADR-0027): advisory make-or-buy, the supplier the
 * requester named, the panel shortlist with what is evidenced and what is not, the spend
 * benchmark with its caveats, and the written summary. Internal: never shown to requesters.
 */
export function SourcingSection({ requestId, catalogue }: { requestId: string; catalogue?: FieldCatalogue }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const sourcing = useSourcing(requestId);
  const refresh = useRefreshSourcing(requestId);
  const analysis = sourcing.data?.analysis ?? null;
  const pending = analysis?.status === "PENDING" || refresh.isPending;
  const dateTime = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });

  return (
    <section aria-labelledby={titleId} className="rounded-lg bg-surface p-4 shadow-card" data-testid="sourcing">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id={titleId} className="font-heading text-base font-semibold text-fg">
            {t("sourcing.title")}
          </h2>
          <p className="text-sm text-fg-muted" aria-live="polite">
            {pending
              ? t("sourcing.preparing")
              : analysis?.builtAt
                ? t("sourcing.builtAt", { at: dateTime.format(new Date(analysis.builtAt)) })
                : t("sourcing.notYet")}
          </p>
        </div>
        <Button variant="secondary" disabled={pending} onClick={() => refresh.mutate()}>
          <RefreshCw aria-hidden="true" className={clsx("h-4 w-4", pending && "motion-safe:animate-spin")} />
          {analysis ? t("sourcing.refresh") : t("sourcing.prepare")}
        </Button>
      </div>

      {sourcing.isError || refresh.isError ? (
        <p role="alert" className="mb-3 rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("sourcing.loadError")}
        </p>
      ) : null}
      {analysis?.status === "FAILED" ? (
        <p role="alert" className="mb-3 rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("sourcing.failed")}
        </p>
      ) : null}
      {analysis?.stale && !pending ? (
        <p className="mb-3 flex items-start gap-2 rounded-sm bg-warning-bg px-3 py-2 text-sm text-warning-fg">
          <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          {t("sourcing.stale")}
        </p>
      ) : null}

      {analysis?.builtAt ? <Analysis analysis={analysis} catalogue={catalogue} /> : null}
      <p className="mt-4 text-xs text-fg-muted">{t("sourcing.internalNote")}</p>
    </section>
  );
}

function Analysis({ analysis, catalogue }: { analysis: SourcingAnalysis; catalogue?: FieldCatalogue }) {
  const { t, i18n } = useTranslation();
  const narrative = analysis.narrative;
  const lang = i18n.language.startsWith("it") ? "it" : "en";
  const labels = new Map(catalogue?.fields.map((f) => [f.key, f.labels[lang]]));
  const mob = analysis.makeOrBuy;
  const named = analysis.namedSupplier;
  return (
    <div className="flex flex-col gap-5">
      {mob ? (
        <Block title={t("sourcing.makeOrBuy.title")}>
          <p className="flex flex-wrap items-center gap-2 text-sm text-fg">
            <span
              className={clsx(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
                mob.recommendation === "INTERNAL" ? "bg-warning-bg text-warning-fg" : "bg-success-bg text-success",
              )}
            >
              {mob.recommendation === "INTERNAL" ? (
                <Users aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Factory aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              {t(`sourcing.makeOrBuy.${mob.recommendation}`)}
            </span>
            <span className="text-fg-muted">{t("sourcing.makeOrBuy.advisory")}</span>
          </p>
          <ul className="mt-2 list-disc pl-5 text-sm text-fg">
            {mob.reasons.map((r) => (
              <li key={r.code}>
                {t(`sourcing.makeOrBuy.reasons.${r.code}`, { resources: r.resources, months: r.months })}
              </li>
            ))}
            {mob.bridgeVendor ? <li>{t("sourcing.makeOrBuy.bridge")}</li> : null}
          </ul>
          {mob.missingInputs.length > 0 ? (
            <p className="mt-2 text-sm text-fg-muted">
              {t("sourcing.makeOrBuy.missing", {
                fields: mob.missingInputs.map((k) => labels.get(k) ?? k).join(", "),
              })}
            </p>
          ) : null}
        </Block>
      ) : null}

      {named && named.status !== "none" ? (
        <Block title={t("sourcing.named.title")}>
          <p className="text-sm text-fg">
            {t(`sourcing.named.${named.status}`, {
              name: named.name,
              match: named.displayName,
              count: named.orderCount,
            })}
            {named.possibleReason ? ` ${t(`admin.suppliers.reason.${named.possibleReason}`)}.` : ""}
          </p>
        </Block>
      ) : null}

      {analysis.shortlist ? (
        <Block title={t("sourcing.shortlist.title")}>
          {analysis.shortlist.outcome !== "candidates" ? (
            <p className="text-sm text-fg">{t(`sourcing.shortlist.outcome.${analysis.shortlist.outcome}`)}</p>
          ) : null}
          {analysis.shortlist.alsoMatching > 0 ? (
            <p className="text-sm text-fg-muted">
              {t("sourcing.shortlist.alsoMatching", { count: analysis.shortlist.alsoMatching })}
            </p>
          ) : null}
          {analysis.shortlist.individualsExcluded > 0 ? (
            <p className="text-sm text-fg-muted">
              {t("sourcing.shortlist.individualsExcluded", { count: analysis.shortlist.individualsExcluded })}
            </p>
          ) : null}
          <ul className="mt-2 flex flex-col gap-3">
            {analysis.shortlist.candidates.map((c) => (
              <CandidateCard key={c.supplierId} candidate={c} narrative={narrative} />
            ))}
          </ul>
        </Block>
      ) : null}

      {analysis.benchmark ? <BenchmarkBlock analysis={analysis} /> : null}

      <Block title={t("sourcing.summary.title")}>
        {narrative ? (
          <div className="flex flex-col gap-2 text-sm text-fg">
            <p>{narrative.panelSummary}</p>
            <p>{narrative.commercialNote}</p>
            <p>
              <span className="font-semibold">{t("sourcing.summary.nextSteps")} </span>
              {narrative.nextSteps}
            </p>
          </div>
        ) : (
          <p className="text-sm text-fg-muted">{t("sourcing.summary.unavailable")}</p>
        )}
      </Block>
    </div>
  );
}

function CandidateCard({ candidate: c, narrative }: { candidate: Candidate; narrative: SourcingNarrative | null }) {
  const { t, i18n } = useTranslation();
  const judged = narrative?.suppliers.find((s) => s.supplierId === c.supplierId);
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  return (
    <li className="min-w-0 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold break-words text-fg">{c.displayName}</span>
        {judged ? (
          <span
            className={clsx(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
              judged.label === "shortlisted" ? "bg-success-bg text-success" : "bg-bg text-fg-muted",
            )}
          >
            {judged.label === "shortlisted" ? (
              <CircleCheck aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {t(`sourcing.labels.${judged.label}`)}
          </span>
        ) : null}
        {c.named ? <span className="text-xs font-semibold text-primary">{t("sourcing.shortlist.named")}</span> : null}
        {c.entityType === "INDIVIDUAL" ? (
          <span className="text-xs text-fg-muted">{t("sourcing.shortlist.individual")}</span>
        ) : null}
      </div>
      <ul className="mt-1 text-sm text-fg">
        {c.rates.map((r) => (
          <li key={`${r.roleLabel}-${r.seniority}-${r.contractType}`}>
            <RateLine rate={r} />
          </li>
        ))}
      </ul>
      {c.activities.length > 0 ? (
        <p className="mt-1 text-sm break-words text-fg-muted">
          {t("sourcing.shortlist.activities", { list: c.activities.join("; ") })}
        </p>
      ) : null}
      <p className="mt-1 text-sm text-fg-muted">
        {c.history.orders > 0
          ? t("sourcing.shortlist.history", {
              count: c.history.orders,
              date: c.history.lastOrderDate ? date.format(new Date(c.history.lastOrderDate)) : "—",
            })
          : t("sourcing.shortlist.noHistory")}
      </p>
      {judged ? (
        <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
          <CriteriaList title={t("sourcing.criteria.met")} items={judged.criteriaMet} met />
          <CriteriaList title={t("sourcing.criteria.unverified")} items={judged.criteriaUnverified} met={false} />
          <p className="text-fg sm:col-span-2">{judged.whyInvite}</p>
        </div>
      ) : null}
    </li>
  );
}

function CriteriaList({ title, items, met }: { title: string; items: string[]; met: boolean }) {
  const { t } = useTranslation();
  const Icon = met ? CircleCheck : CircleHelp;
  return (
    <div className="min-w-0">
      <p className="font-semibold text-fg-muted">{title}</p>
      {items.length === 0 ? (
        <p className="text-fg-muted">{t("sourcing.criteria.none")}</p>
      ) : (
        <ul>
          {items.map((i) => (
            <li key={i} className="flex items-start gap-1 text-fg">
              <Icon
                aria-hidden="true"
                className={clsx("mt-0.5 h-4 w-4 shrink-0", met ? "text-success" : "text-fg-muted")}
              />
              <span className="break-words">{i}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RateLine({ rate }: { rate: RateRange }) {
  const { t, i18n } = useTranslation();
  const eur = new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  const who = [rate.roleLabel, rate.seniority ? t(`sourcing.seniority.${rate.seniority}`) : null]
    .filter(Boolean)
    .join(" · ");
  if (rate.contractType === "VALUE") return <>{t("sourcing.rates.value", { who })}</>;
  if (rate.min === null || rate.max === null) return <>{t("sourcing.rates.unknown", { who })}</>;
  const amount = rate.min === rate.max ? eur.format(rate.min) : `${eur.format(rate.min)}–${eur.format(rate.max)}`;
  return <>{t("sourcing.rates.day", { who, amount })}</>;
}

function BenchmarkBlock({ analysis }: { analysis: SourcingAnalysis }) {
  const { t, i18n } = useTranslation();
  const b = analysis.benchmark;
  if (!b) return null;
  const eur = new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  const day = (d: string | null) => (d ? date.format(new Date(d)) : "—");
  const scopes = [b.category, ...b.suppliers].filter((s): s is BenchmarkScope => s !== null);
  const thin = scopes.filter((s) => !s.enough && s.orders > 0);
  return (
    <Block title={t("sourcing.benchmark.title")}>
      {b.categories.length === 0 ? <p className="text-sm text-fg">{t("sourcing.benchmark.noCategory")}</p> : null}
      <ul className="flex flex-col gap-2 text-sm text-fg">
        {scopes
          .filter((s) => s.enough)
          .map((s) => (
            <li key={`${s.scope}-${s.label}`}>
              <span className="font-semibold">{s.label}</span>
              {": "}
              {t(s.scope === "category" ? "sourcing.benchmark.categoryLine" : "sourcing.benchmark.supplierLine", {
                count: s.orders,
                suppliers: s.suppliers,
                from: day(s.firstOrderDate),
                to: day(s.lastOrderDate),
                min: eur.format(s.min ?? 0),
                max: eur.format(s.max ?? 0),
                median: eur.format(s.median ?? 0),
                p25: eur.format(s.p25 ?? 0),
                p75: eur.format(s.p75 ?? 0),
              })}
              {b.budget !== null && s.atOrAboveBudget !== null
                ? ` ${t("sourcing.benchmark.atSize", { at: s.atOrAboveBudget, count: s.orders, budget: eur.format(b.budget) })}`
                : ""}
            </li>
          ))}
        {b.categories.length > 0 && (!b.category || b.category.orders === 0) ? (
          <li>{t("sourcing.benchmark.noCategoryOrders", { categories: b.categories.join(" / ") })}</li>
        ) : null}
      </ul>
      {thin.length > 0 ? (
        <p className="mt-2 text-sm text-fg">
          {t("sourcing.benchmark.thin", { list: thin.map((s) => `${s.label} (${s.orders})`).join(", ") })}
        </p>
      ) : null}
      {b.withoutHistory.length > 0 ? (
        <p className="mt-2 text-sm text-fg">
          {t("sourcing.benchmark.withoutHistory", { list: b.withoutHistory.map((s) => s.displayName).join(", ") })}
        </p>
      ) : null}
      {b.band ? (
        <p className="mt-2 text-xs text-fg-muted">
          {t("sourcing.benchmark.band", { min: eur.format(b.band.min), max: eur.format(b.band.max) })}
        </p>
      ) : null}
      {b.budget !== null ? (
        <p className="mt-1 text-xs text-fg-muted">
          {t(b.budgetApproved ? "sourcing.benchmark.budgetApproved" : "sourcing.benchmark.budgetIndicative", {
            budget: eur.format(b.budget),
          })}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-fg-muted">{t("sourcing.benchmark.caveats")}</p>
    </Block>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="min-w-0">
      <h3 id={id} className="mb-1 text-sm font-semibold text-fg-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}
